import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Button, Modal, Form, Alert, Badge,Offcanvas } from 'react-bootstrap';
import { useAuth } from '../../context/AuthContext';
import withPermission from '../../utils/withPermission';
import Swal from 'sweetalert2';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { format, isBefore, isAfter, parseISO } from 'date-fns';

const backendUrl = process.env.REACT_APP_BACKEND_URL;
const API_URL = `${backendUrl}/api/accounting`;

const FinancialLock = () => {
    const [lockedPeriods, setLockedPeriods] = useState([]);
    const [showLockModal, setShowLockModal] = useState(false);
    const [showUnlockModal, setShowUnlockModal] = useState(false);
    const [formData, setFormData] = useState({
        year_label: '',
        start_date: null,
        end_date: null
    });
    const [unlockReason, setUnlockReason] = useState('');
    const [selectedPeriod, setSelectedPeriod] = useState(null);
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [currentDate] = useState(new Date());

    // Permission wrappers
    const WithListPermission = withPermission(({ children }) => children, ['lock-list']);
    const WithCreatePermission = withPermission(({ children }) => children, ['lock-create']);
    const WithUnlockPermission = withPermission(({ children }) => children, ['lock-unlock']);

    useEffect(() => {
        fetchLockedPeriods();
    }, []);

    const fetchLockedPeriods = async () => {
        try {
            const userInfo = JSON.parse(localStorage.getItem('user-info'));
            const origin = localStorage.getItem('user-origin');
            const token = userInfo?.token;

            const response = await axios.get(`${API_URL}/locked-periods`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Tenant-Origin': origin,
                }
            });

            setLockedPeriods(response.data.locked_periods);
        } catch (error) {
            console.error('Error fetching locked periods:', error);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Failed to load locked periods'
            });
        }
    };

    const handleAddLock = () => {
        setFormData({
            year_label: '',
            start_date: null,
            end_date: null
        });
        setErrors({});
        setShowLockModal(true);
    };

    const handleLockYear = async () => {
        const validationErrors = validateLockForm();
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            return;
        }

        setLoading(true);
        try {
            const userInfo = JSON.parse(localStorage.getItem('user-info'));
            const origin = localStorage.getItem('user-origin');
            const token = userInfo?.token;

            const payload = {
                ...formData,
                start_date: format(formData.start_date, 'yyyy-MM-dd'),
                end_date: format(formData.end_date, 'yyyy-MM-dd')
            };

            const response = await axios.post(`${API_URL}/lock-year`, payload, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Tenant-Origin': origin,
                }
            });

            Swal.fire({
                icon: 'success',
                title: 'Success',
                text: response.data.message,
                timer: 2000,
                showConfirmButton: false
            });

            setShowLockModal(false);
            fetchLockedPeriods();
        } catch (error) {
            console.error('Error locking year:', error);
            const errorMsg = error.response?.data?.message || 'Failed to lock financial year';
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: errorMsg
            });
        } finally {
            setLoading(false);
        }
    };

    const handleUnlockClick = (period) => {
        setSelectedPeriod(period);
        setUnlockReason('');
        setShowUnlockModal(true);
    };

    const handleUnlockYear = async () => {
        if (!unlockReason.trim()) {
            Swal.fire({
                icon: 'warning',
                title: 'Warning',
                text: 'Please provide a reason for unlocking'
            });
            return;
        }

        setLoading(true);
        try {
            const userInfo = JSON.parse(localStorage.getItem('user-info'));
            const origin = localStorage.getItem('user-origin');
            const token = userInfo?.token;

            const response = await axios.post(
                `${API_URL}/unlock-year/${selectedPeriod.id}`,
                { reason: unlockReason },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Tenant-Origin': origin,
                    }
                }
            );

            Swal.fire({
                icon: 'success',
                title: 'Success',
                text: response.data.message,
                timer: 2000,
                showConfirmButton: false
            });

            setShowUnlockModal(false);
            fetchLockedPeriods();
        } catch (error) {
            console.error('Error unlocking year:', error);
            const errorMsg = error.response?.data?.message || 'Failed to unlock financial year';
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: errorMsg
            });
        } finally {
            setLoading(false);
        }
    };

    const validateLockForm = () => {
        const newErrors = {};
        
        if (!formData.year_label.trim()) {
            newErrors.year_label = 'Year label is required';
        }
        
        if (!formData.start_date) {
            newErrors.start_date = 'Start date is required';
        }
        
        if (!formData.end_date) {
            newErrors.end_date = 'End date is required';
        }
        
        if (formData.start_date && formData.end_date && isAfter(formData.start_date, formData.end_date)) {
            newErrors.end_date = 'End date must be after start date';
        }

        return newErrors;
    };

    const formatDate = (dateString) => {
        return format(parseISO(dateString), 'dd MMM yyyy');
    };

    const isDateInLockedPeriod = (date) => {
        return lockedPeriods.some(period => {
            const start = parseISO(period.start_date);
            const end = parseISO(period.end_date);
            return isAfter(date, start) && isBefore(date, end);
        });
    };

    return (
        <div className="main-content">
            <div className="page-content">
                <div className="container-fluid">
                    <div className="row">
                        <div className="col-12">
                            <div className="page-title-box d-sm-flex align-items-center justify-content-between">
                                <h4 className="mb-sm-0 font-size-18">Financial Year Lock</h4>
                                <div className="page-title-right">
                                    <ol className="breadcrumb m-0">
                                        <li className="breadcrumb-item">
                                            <a href="javascript:void(0);">Accounting</a>
                                        </li>
                                        <li className="breadcrumb-item active">Locked Periods</li>
                                    </ol>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Alert Section */}
                    <div className="row mb-3">
                        <div className="col-12">
                            <Alert variant={isDateInLockedPeriod(currentDate) ? 'danger' : 'success'}>
                                <div className="d-flex align-items-center">
                                    <i className={`mdi mdi-${isDateInLockedPeriod(currentDate) ? 'lock' : 'lock-open'} me-2`}></i>
                                    <span>
                                        <strong>Current Status:</strong>{' '}
                                        {isDateInLockedPeriod(currentDate) 
                                            ? `Current date (${format(currentDate, 'dd MMM yyyy')}) is within a locked period. Transactions are restricted.`
                                            : `Current date (${format(currentDate, 'dd MMM yyyy')}) is not locked. Transactions are allowed.`
                                        }
                                    </span>
                                </div>
                            </Alert>
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-12">
                            <div className="card">
                                <div className="card-body">
                                    <div className="d-flex justify-content-between mb-3">
                                        <div>
                                            <h5 className="card-title">Locked Financial Periods</h5>
                                            <p className="text-muted">Manage financial year locks to restrict transactions</p>
                                        </div>
                                        <WithCreatePermission>
                                            <Button 
                                                variant="primary" 
                                                onClick={handleAddLock}
                                                className="d-flex align-items-center gap-2"
                                            >
                                                <i className="mdi mdi-lock-plus"></i>
                                                Lock New Period
                                            </Button>
                                        </WithCreatePermission>
                                    </div>

                                    <div className="table-responsive">
                                        <table className="table table-hover table-nowrap">
                                            <thead className="table-sales-gradient">
                                                <tr>
                                                    <th>#</th>
                                                    <th>Year Label</th>
                                                    <th>Start Date</th>
                                                    <th>End Date</th>
                                                    <th>Locked At</th>
                                                    <th>Locked By</th>
                                                    <th>Status</th>
                                                    <th>Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {lockedPeriods.length === 0 ? (
                                                    <tr>
                                                        <td colSpan="8" className="text-center py-4">
                                                            <div className="text-muted">
                                                                <i className="mdi mdi-lock-open-outline display-6"></i>
                                                                <p className="mt-2">No locked periods found</p>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ) : (
                                                    lockedPeriods.map((period, index) => (
                                                        <tr key={period.id}>
                                                            <td>{index + 1}</td>
                                                            <td>
                                                                <strong>{period.year_label}</strong>
                                                            </td>
                                                            <td>{formatDate(period.start_date)}</td>
                                                            <td>{formatDate(period.end_date)}</td>
                                                            <td>
                                                                {format(parseISO(period.locked_at), 'dd MMM yyyy HH:mm')}
                                                            </td>
                                                            <td>
                                                                {period.locked_by_user?.name || 'N/A'}
                                                            </td>
                                                            <td>
                                                                <Badge bg="danger">
                                                                    <i className="mdi mdi-lock me-1"></i>
                                                                    Locked
                                                                </Badge>
                                                            </td>
                                                            <td>
                                                                <WithUnlockPermission>
                                                                    <Button
                                                                        variant="outline-danger"
                                                                        size="sm"
                                                                        onClick={() => handleUnlockClick(period)}
                                                                        className="d-flex align-items-center gap-1"
                                                                    >
                                                                        <i className="mdi mdi-lock-open"></i>
                                                                        Unlock
                                                                    </Button>
                                                                </WithUnlockPermission>
                                                            </td>
                                                        </tr>
                                                    ))
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Lock Period Modal */}
                    <Offcanvas show={showLockModal} onHide={() => setShowLockModal(false)} style={{ width: '500px' }} placement="end">
                        <Offcanvas.Header closeButton>
                            <Offcanvas.Title>Lock Financial Period</Offcanvas.Title>
                        </Offcanvas.Header>
                        <Offcanvas.Body>
                            <Form>
                                <Form.Group className="mb-3">
                                    <Form.Label>Year Label *</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={formData.year_label}
                                        onChange={(e) => setFormData({...formData, year_label: e.target.value})}
                                        placeholder="e.g., FY 2023-2024"
                                        className={errors.year_label ? 'is-invalid' : ''}
                                    />
                                    {errors.year_label && (
                                        <div className="invalid-feedback">{errors.year_label}</div>
                                    )}
                                </Form.Group>

                                <div className="row">
                                    <div className="col-md-6">
                                        <Form.Group className="mb-3">
                                            <Form.Label>Start Date *</Form.Label>
                                            <DatePicker
                                                selected={formData.start_date}
                                                onChange={(date) => setFormData({...formData, start_date: date})}
                                                className={`form-control ${errors.start_date ? 'is-invalid' : ''}`}
                                                dateFormat="dd/MM/yyyy"
                                                placeholderText="Select start date"
                                            />
                                            {errors.start_date && (
                                                <div className="invalid-feedback">{errors.start_date}</div>
                                            )}
                                        </Form.Group>
                                    </div>
                                    <div className="col-md-6">
                                        <Form.Group className="mb-3">
                                            <Form.Label>End Date *</Form.Label>
                                            <DatePicker
                                                selected={formData.end_date}
                                                onChange={(date) => setFormData({...formData, end_date: date})}
                                                className={`form-control ${errors.end_date ? 'is-invalid' : ''}`}
                                                dateFormat="dd/MM/yyyy"
                                                placeholderText="Select end date"
                                                minDate={formData.start_date}
                                            />
                                            {errors.end_date && (
                                                <div className="invalid-feedback">{errors.end_date}</div>
                                            )}
                                        </Form.Group>
                                    </div>
                                </div>

                                <Alert variant="warning" className="mt-3">
                                    <i className="mdi mdi-alert-circle-outline me-2"></i>
                                    <strong>Warning:</strong> Once locked, transactions within this period cannot be created, modified, or deleted until unlocked.
                                </Alert>
                            </Form>
                        </Offcanvas.Body>
                        <div className="offcanvas-footer p-3 border-top d-flex justify-content-end me-2">
                            <Button variant="secondary" onClick={() => setShowLockModal(false)}>
                                Cancel
                            </Button>
                            <Button 
                                variant="primary" 
                                onClick={handleLockYear}
                                disabled={loading}
                            >
                                {loading ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-2"></span>
                                        Locking...
                                    </>
                                ) : (
                                    'Lock Period'
                                )}
                            </Button>
                        </div>
                    </Offcanvas>

                    {/* Unlock Modal */}
                    <Offcanvas show={showUnlockModal} onHide={() => setShowUnlockModal(false)} size="lg" placement="end">
                        <Offcanvas.Header closeButton>
                            <Offcanvas.Title>Unlock Financial Period</Offcanvas.Title>
                        </Offcanvas.Header>
                        <Offcanvas.Body>
                            {selectedPeriod && (
                                <div className="mb-3">
                                    <p>
                                        You are about to unlock: <strong>{selectedPeriod.year_label}</strong>
                                    </p>
                                    <p className="text-muted">
                                        Period: {formatDate(selectedPeriod.start_date)} to {formatDate(selectedPeriod.end_date)}
                                    </p>
                                </div>
                            )}
                            
                            <Form.Group>
                                <Form.Label>Reason for Unlocking *</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={3}
                                    value={unlockReason}
                                    onChange={(e) => setUnlockReason(e.target.value)}
                                    placeholder="Please provide a reason for unlocking this period..."
                                />
                                <Form.Text className="text-muted">
                                    This action will be logged in the audit trail.
                                </Form.Text>
                            </Form.Group>

                            <Alert variant="danger" className="mt-3">
                                <i className="mdi mdi-alert me-2"></i>
                                <strong>Important:</strong> Unlocking will allow transactions to be created, modified, or deleted within this period.
                            </Alert>
                        </Offcanvas.Body>
                        <div className="offcanvas-footer p-3 border-top d-flex justify-content-end">
                            <Button variant="secondary" onClick={() => setShowUnlockModal(false)}>
                                Cancel
                            </Button>
                            <Button 
                                variant="danger" 
                                onClick={handleUnlockYear}
                                disabled={loading || !unlockReason.trim()}
                            >
                                {loading ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-2"></span>
                                        Unlocking...
                                    </>
                                ) : (
                                    'Confirm Unlock'
                                )}
                            </Button>
                        </div>
                    </Offcanvas>
                </div>
            </div>
        </div>
    );
};

export default FinancialLock;
